import { generatePath } from 'react-router-dom';
import { AppRoutes } from 'constants/AppRoutes';

import DOSSIER_FACT_TYPES from './dossierFactTypes';

import type IAiChatScreen from 'components/AiChat/interfaces/IAiChatScreen';
import type { WatchCompany, WatchFinding, WatchIssueChatMessage } from './types';

const NO_SUB_SCREENS: IAiChatScreen['subScreens'] = [];

/** Nest rejects chatContext over 4000 serialized characters. Keep task facts short. */
const CHIP_TEXT_MAX = 240;
const SEED_TURN_MAX = 2;
const SEED_TEXT_MAX = 220;
const FINDING_LIST_MAX = 8;

const clip = (text: string, max: number): string => (
  text.length <= max ? text : `${text.slice(0, max)}…`
);

function slimValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return clip(value, CHIP_TEXT_MAX);
  }
  if (Array.isArray(value)) {
    return value.slice(0, FINDING_LIST_MAX).map(slimValue);
  }
  if (value && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    Object.keys(source).forEach((key) => {
      if (key === 'payloadJson') {
        const raw = source[key];
        const text = typeof raw === 'string' ? raw : JSON.stringify(raw);
        out[key] = clip(text, CHIP_TEXT_MAX);
        return;
      }
      out[key] = slimValue(source[key]);
    });
    return out;
  }
  return value;
}

/**
 * Screen payload for ATT-144 send. Task facts travel in customData for this turn only.
 * Nest does not store chatContext. The bound chat id is the durable history.
 */
export function issueChatScreen(
  finding: WatchFinding,
  chips: Record<string, unknown>,
  seedTurns: WatchIssueChatMessage[],
): IAiChatScreen {
  return {
    screen: 'marketingOpsFinding',
    label: finding.title,
    url: generatePath(AppRoutes.marketingOpsFinding, { publicId: finding.publicId }),
    entities: [
      { type: 'findingPublicId', id: finding.publicId, label: finding.title },
    ],
    customData: {
      taskKind: 'issue_chat',
      severity: finding.severity,
      chips: slimValue(chips),
      seedTurns: seedTurns.slice(0, SEED_TURN_MAX).map((item) => ({
        role: item.role,
        text: clip(item.contentText, SEED_TEXT_MAX),
      })),
    },
    subScreens: NO_SUB_SCREENS,
  };
}

export function createCompanyChatScreen(): IAiChatScreen {
  return {
    screen: 'watchTower',
    label: 'New company',
    url: AppRoutes.watchTower,
    customData: { taskKind: 'create_company' },
    subScreens: NO_SUB_SCREENS,
  };
}

export function dossierChatScreen(
  company: WatchCompany,
  context: Record<string, unknown>,
  stepContext?: Record<string, unknown>,
): IAiChatScreen {
  return {
    screen: 'watchTowerCompany',
    label: company.name,
    url: generatePath(AppRoutes.watchTowerCompany, { id: String(company.id) }),
    entities: [
      { type: 'watchCompanyId', id: company.id, label: company.name },
    ],
    customData: {
      taskKind: 'company_dossier',
      uid: company.uid,
      addressCountry: company.addressCountry,
      factTypes: DOSSIER_FACT_TYPES,
      findings: Array.isArray(context.findings)
        ? context.findings.slice(0, FINDING_LIST_MAX)
        : [],
      ...stepContext,
    },
    subScreens: NO_SUB_SCREENS,
  };
}
